import { sharedTools } from './shared-tools';
import { fileTools } from './file-tools';
import type { OfficeToolDefinition } from './shared-tools';

export type { OfficeToolDefinition };

/** Cached tools for the detected host — computed once, reused on every call. */
let _cachedHostTools: OfficeToolDefinition[] | null = null;
let _cachedHostType: typeof Office.HostType[keyof typeof Office.HostType] | null = null;

/**
 * Returns the Office.js tools available for the current host application.
 * Only dynamically imports the tool module for the detected host
 * (Word OR Excel OR PowerPoint OR Outlook — never all of them).
 * Result is cached so subsequent calls resolve instantly.
 * Falls back to sharedTools + fileTools (never empty).
 */
export async function getHostTools(): Promise<OfficeToolDefinition[]> {
  try {
    const host = Office?.context?.host;
    if (!host) {
      console.warn('[OfficeToolsRegistry] Office.context.host is undefined — returning shared tools only');
      return [...sharedTools, ...fileTools];
    }

    // Return cached tools if we already resolved for this host
    if (_cachedHostTools && _cachedHostType === host) {
      return _cachedHostTools;
    }

    let hostSpecificTools: OfficeToolDefinition[];

    switch (host) {
      case Office.HostType.Word: {
        const { wordTools } = await import('./word-tools');
        hostSpecificTools = [...sharedTools, ...wordTools, ...fileTools];
        break;
      }
      case Office.HostType.Excel: {
        const { excelTools } = await import('./excel-tools');
        hostSpecificTools = [...sharedTools, ...excelTools, ...fileTools];
        break;
      }
      case Office.HostType.PowerPoint: {
        const { powerpointTools } = await import('./powerpoint-tools');
        hostSpecificTools = [...sharedTools, ...powerpointTools, ...fileTools];
        break;
      }
      case Office.HostType.Outlook: {
        const { outlookTools } = await import('./outlook-tools');
        hostSpecificTools = [...outlookTools, ...fileTools];
        break;
      }
      default:
        hostSpecificTools = [...sharedTools, ...fileTools];
    }

    // Cache so subsequent calls are instant
    _cachedHostType = host;
    _cachedHostTools = hostSpecificTools;
    return hostSpecificTools;
  } catch (err) {
    console.error('[OfficeToolsRegistry] Failed to load host tools:', err);
    return [...sharedTools, ...fileTools];
  }
}

/**
 * Synchronous getter — returns cached tools if available, otherwise fallback.
 * Use after the initial async getHostTools() has resolved at least once.
 */
export function getHostToolsSync(): OfficeToolDefinition[] {
  if (_cachedHostTools) return _cachedHostTools;
  return [...sharedTools, ...fileTools];
}

export function getHostName(): string {
  try {
    const host = Office?.context?.host;
    if (!host) return 'Office';

    switch (host) {
      case Office.HostType.Word:
        return 'Word';
      case Office.HostType.Excel:
        return 'Excel';
      case Office.HostType.PowerPoint:
        return 'PowerPoint';
      case Office.HostType.Outlook:
        return 'Outlook';
      default:
        return 'Office';
    }
  } catch {
    return 'Office';
  }
}
